import { useMemo, useState } from 'react';

import {
  Box,
  Button,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalButtonGroup,
  ModalCloseButton,
  Text,
  VStack,
  useDisclosure,
  useTheme,
  ButtonGroup,
} from '@frontend/components';

import { ArticlePageTemplate } from 'components/ArticlePageTemplate';
import { PageTitle } from 'components/PageTitle';

import { buildGirlPodStyles } from 'modules/quotes/quoteItemComponents/bomBuilder/hooks/useEasterEgg';

const SILLY_MESSAGES = [
  {
    label: '🎱 Ask the Magic 8-Ball',
    title: '🎱 The Magic 8-Ball Says...',
    answers: [
      'It is certain.',
      'Reply hazy, try again.',
      "Don't count on it.",
      'My sources say no.',
      'Outlook good.',
      'Ask again after lunch.',
      "Signs point to yes... but I'm just a ball.",
      'Better not tell you now. Actually, fine: no.',
    ],
  },
  {
    label: "🍕 What's for Lunch?",
    title: "🍕 Today's Lunch Verdict",
    answers: [
      'Leftover pizza. Again.',
      'A "salad" that is 90% croutons.',
      'Whatever the person next to you is having.',
      'Just coffee. Coffee counts.',
      "Al's?",
      'Cereal. No judgment.',
    ],
  },
  {
    label: '🐛 Ship a Bug',
    title: '🐛 Bug Successfully Shipped!',
    answers: [
      "Congratulations! You've deployed an off-by-one error to production.",
      'A wild NullPointerException has appeared in staging!',
      'Successfully replaced all semicolons with Greek question marks (;).',
      'Your CSS now only works on screens held at exactly 43 degrees.',
      'The tests pass! ...because you deleted them.',
      'Deployed! The console is now 40% warnings. Up from 38%.',
    ],
  },
  {
    label: '💡 Wisdom of the Day',
    title: '💡 Ancient Developer Wisdom',
    answers: [
      'A deploy on Friday keeps the weekend busy.',
      'There are only two hard things: cache invalidation, naming things, and off-by-one errors.',
      'It works on my machine. Ship my machine.',
      'The best code is no code at all.',
      "If it's stupid but it works, it's still stupid and you got lucky.",
      'Always code as if the next person to maintain it is a violent psychopath who knows where you live.',
    ],
  },
];

const pick = <T,>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];

export const MultiSiteDashboard = () => {
  const theme = useTheme();
  const styles = useMemo(() => buildGirlPodStyles(theme), [theme]);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [modalContent, setModalContent] = useState({ title: '', body: '' });

  const handleClick = (category: (typeof SILLY_MESSAGES)[number]) => {
    setModalContent({ title: category.title, body: pick(category.answers) });
    onOpen();
  };

  return (
    <Box style={styles} padding={4} bg="page.background" minHeight="100vh">
      <PageTitle title="🥷 Multi-Site Dashboard" />
      <ArticlePageTemplate
        title="🥷 Multi-Site Dashboard"
        helpByline="Let's go to the moon!"
      >
        <VStack spacing={4} align="stretch">
          <Text size="md">
            Important multi-site business tools below. Use responsibly.
          </Text>
          <ButtonGroup>
            {SILLY_MESSAGES.map((category) => (
              <Button
                key={category.label}
                onClick={() => handleClick(category)}
              >
                {category.label}
              </Button>
            ))}
          </ButtonGroup>
        </VStack>
      </ArticlePageTemplate>

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalContent style={styles}>
          <ModalHeader>
            {modalContent.title}
            <ModalCloseButton />
          </ModalHeader>
          <ModalBody>
            <Text size="lg">{modalContent.body}</Text>
          </ModalBody>
          <ModalFooter>
            <ModalButtonGroup>
              <Button onClick={onClose} variant="primary">
                Noted. 🫡
              </Button>
            </ModalButtonGroup>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};
